/**
 * Permissions v2 resolver — one algorithm, shared by client and server.
 *
 * ⚠️ AUTHORED source. `functions/permissionsCore.js` is a GENERATED CommonJS
 * mirror (`node scripts/syncPermissionsRegistry.js`); the sync test fails when
 * it is stale. Only top-level `export const/function` and the single
 * registry import below are allowed (the generator rewrites them).
 *
 * Resolution order (muti_clinic_update.md §2.2):
 *   0. unknown key                                   → false (dev warning)
 *   1. clinic-master key                             → clinic-level value only
 *      (no admin bypass: e.g. allowMultipleTherapists is a clinic fact)
 *   2. admin (role === "owner" || isAdmin === true)  → true
 *   3. therapist.permissions[key] defined            → v !== false && v !== null
 *   4. clinic.therapistPermissions[key] defined      → v !== false && v !== null
 *      (+ legacy per-integration fallback for allowCreateInvoices)
 *   5. registry legacyDefault                        (== today's behavior)
 *   `requires`: a key whose prerequisite resolves false resolves false.
 *
 * For every existing (legacy) document step 3 is skipped, so every key
 * resolves exactly as the pre-v2 selectors did.
 */
import {
  PERMISSIONS_BY_KEY,
  PER_THERAPIST_PERMISSION_KEYS,
  isAdminTherapist,
  isPerTherapistPermissionKey,
  legacyInvoiceFallback,
} from "../config/permissionsRegistry";

function boolOn(v) {
  return v !== false && v !== null;
}

function warnUnknownKey(key) {
  if (typeof console !== "undefined" && console.warn) {
    console.warn("[CK-5590] resolvePermission: unknown permission key", { key });
  }
}

function resolveClinicMaster(entry, clinic) {
  const bag = clinic?.[entry.storage];
  const raw = bag ? bag[entry.field] : undefined;
  if (entry.type === "enum") {
    return entry.values.includes(raw) ? raw : entry.default;
  }
  if (entry.key === "allowMultipleTherapists") {
    // Opt-in; never on for a solo clinic (mirrors selectAllowMultipleTherapists).
    if (clinic?.isSoloClinic) return false;
    return raw === true;
  }
  if (raw === undefined) return entry.legacyDefault;
  return boolOn(raw);
}

/**
 * Resolve one permission key for a therapist in a clinic.
 * @param {string} key - registry key
 * @param {object|null} therapist - /therapists/{uid} data
 * @param {object|null} clinic - /clinics/{id} data
 * @returns {boolean|string} boolean for every key except enum master keys
 */
export function resolvePermission(key, therapist, clinic) {
  const entry = PERMISSIONS_BY_KEY[key];
  if (!entry) {
    warnUnknownKey(key);
    return false;
  }
  if (entry.clinicMaster) return resolveClinicMaster(entry, clinic);
  if (isAdminTherapist(therapist)) return true;

  if (entry.requires && resolvePermission(entry.requires, therapist, clinic) !== true) {
    return false;
  }

  const tv = therapist?.permissions ? therapist.permissions[key] : undefined;
  if (tv !== undefined) return boolOn(tv);

  const cv = clinic?.therapistPermissions ? clinic.therapistPermissions[key] : undefined;
  if (cv !== undefined) return boolOn(cv);

  if (key === "allowCreateInvoices") {
    const legacy = legacyInvoiceFallback(clinic);
    if (legacy !== undefined) return legacy;
  }

  return entry.legacyDefault === true;
}

/** Map of every per-therapist key → effective boolean. */
export function getEffectivePermissions(therapist, clinic) {
  const out = {};
  PER_THERAPIST_PERMISSION_KEYS.forEach((key) => {
    out[key] = resolvePermission(key, therapist, clinic);
  });
  return out;
}

/**
 * Keys an admin EXPLICITLY changed away from the role baseline, as stored on
 * the therapist doc (`permissionsOverridden`). Legacy docs ⇒ []. Unknown /
 * clinic-master keys are ignored.
 */
export function getStoredOverriddenKeys(therapist) {
  const raw = therapist?.permissionsOverridden;
  if (!Array.isArray(raw)) return [];
  return raw.filter((k) => typeof k === "string" && isPerTherapistPermissionKey(k));
}

/**
 * True when the admin explicitly set this key for the member (source of truth:
 * `therapist.permissionsOverridden`). Drives the "הרשאות מותאמות" / "אישי"
 * chips. Admins never have personal overrides. `clinic` is accepted for
 * signature compatibility only.
 */
export function isPermissionOverridden(key, therapist, clinic) {
  if (!PERMISSIONS_BY_KEY[key] || PERMISSIONS_BY_KEY[key].clinicMaster) return false;
  if (isAdminTherapist(therapist)) return false;
  return getStoredOverriddenKeys(therapist).includes(key);
}

/** Keys the admin explicitly overrode for this member (registry order). */
export function getOverriddenPermissionKeys(therapist, clinic) {
  return PER_THERAPIST_PERMISSION_KEYS.filter((key) =>
    isPermissionOverridden(key, therapist, clinic),
  );
}
